"use client"

/**
 * Authentication utilities shared across AskFinz apps.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * SECURITY V-029 / V-061 — MIGRATION IN PROGRESS (PARTIAL)
 * ─────────────────────────────────────────────────────────────────────────
 *
 * As of 2026-05-09 the JWT is ALSO set as an HttpOnly+Secure+SameSite=Lax
 * cookie (`AskFinz_auth_token`, Domain=.askfinz.ai) by every backend
 * endpoint that mints a token (POST /api/auth/verify-otp, /verify-2fa,
 * /verify-signup-otp; cleared by /logout). The browser sends it
 * automatically with `credentials: 'include'`, JS can't read it, and a
 * single-subdomain XSS no longer hands an attacker every other subdomain.
 *
 * However we have NOT yet ripped out the localStorage tokens because:
 *
 *   1. The multi-account device-session flow (multi-account.ts) tracks up
 *      to 4 identities by storing each one's JWT — the browser only sends
 *      the *active* HttpOnly cookie, so we still need a JS-readable copy
 *      of the inactive tokens to switch between accounts.
 *
 *   2. Cross-subdomain handoff today happens via `?token=` / `?add_token=`
 *      URL params (V-008). That path is being replaced with one-shot HMAC
 *      handoff codes in a separate wave; until then the receiving app
 *      writes the token to localStorage to bootstrap its session.
 *
 *   3. The 22 individual app `lib/auth.ts` copies still expect a readable
 *      token via `getAuthToken()`. They will inherit cookie-only behaviour
 *      once they're migrated to import from this file (or the V-008 wave
 *      delivers a server-side `/api/auth/me` everywhere).
 *
 * TODO (V-008 wave): once handoff codes ship, delete every localStorage
 *   token write in this file + multi-account.ts and rely entirely on the
 *   server cookie + per-identity refresh-token cookies for switching.
 * TODO (post-V-008): remove document.cookie writes for the active JWT in
 *   setAuthCookie() — the server is now authoritative.
 *
 * In the meantime: any new code SHOULD use `credentials: 'include'` +
 * the server cookie for API calls, and only consult `getAuthToken()` when
 * it genuinely needs the token in JS (e.g. multi-account switching, query
 * param construction for legacy endpoints).
 * ─────────────────────────────────────────────────────────────────────────
 */

const AUTH_TOKEN_KEY = 'AskFinz_auth_token'
const AUTH_USER_KEY = 'AskFinz_auth_user'

/** Cookie name used by middleware to allow requests (must match auth-middleware.ts). */
export const AUTH_COOKIE_NAME = 'AskFinz_auth_token'

/** SECURITY V-029: non-HttpOnly fingerprint cookie set by the server (or by
 * us, for legacy paths) to advertise "there is a session" without exposing
 * the JWT itself. UI code should consult this instead of `getAuthToken()`
 * when it only needs to answer "is the user logged in?". */
export const AUTH_SESSION_MARKER_COOKIE = 'AskFinz_session_marker'

/** Login app base URL (where to redirect after logout). Use NEXT_PUBLIC_LOGIN_APP_URL. */
export function getLoginAppUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_LOGIN_APP_URL) {
        return process.env.NEXT_PUBLIC_LOGIN_APP_URL.replace(/\/$/, '')
    }
    return 'http://localhost:9500'
}

/** Account app base URL. Use NEXT_PUBLIC_ACCOUNT_APP_URL. */
export function getAccountAppUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ACCOUNT_APP_URL) {
        return process.env.NEXT_PUBLIC_ACCOUNT_APP_URL.replace(/\/$/, '')
    }
    return 'http://localhost:9518'
}

/** Main landing app base URL. Use NEXT_PUBLIC_SITE_URL. */
export function getMainAppUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SITE_URL) {
        return process.env.NEXT_PUBLIC_SITE_URL.replace(/\/$/, '')
    }
    return 'http://localhost:9517'
}

/** Backend API base URL for auth (e.g. logout). Use NEXT_PUBLIC_API_URL. */
export function getApiUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) {
        return process.env.NEXT_PUBLIC_API_URL.replace(/\/$/, '')
    }
    return 'http://localhost:9001'
}

/** Centralized Account API URL (where profile/preferences live). */
export function getAccountApiUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ACCOUNT_API_URL) {
        return process.env.NEXT_PUBLIC_ACCOUNT_API_URL.replace(/\/$/, '')
    }

    // In production environment (askfinz.ai subdomains)
    if (typeof window !== 'undefined' && window.location.hostname.endsWith('askfinz.ai')) {
        return 'https://account.askfinz.ai'
    }

    // Local dev fallback (Account Backend Port is 9000)
    return 'http://localhost:9000'
}

/** Centralized Research API URL (library RAG, research workflows). */
export function getResearchApiUrl(): string {
    if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_RESEARCH_API_URL) {
        return process.env.NEXT_PUBLIC_RESEARCH_API_URL.replace(/\/$/, '')
    }
    if (typeof window !== 'undefined' && window.location.hostname.endsWith('askfinz.ai')) {
        return 'https://research-api.askfinz.ai'
    }
    // Local dev fallback (Research Backend Port is 9009)
    return 'http://localhost:9009'
}

/** Redirect to the login app. Does NOT call clearAuthToken() — callers that
 *  need a full session wipe (explicit logout) should call clearAuthToken()
 *  themselves before this.  Background-API 401s must NOT clear the domain-wide
 *  session marker: doing so logs every other open *.askfinz.ai tab out
 *  synchronously, before the browser has even navigated away. */
export function redirectToLoginApp(): void {
    if (typeof window !== 'undefined') {
        const redirectUrl = encodeURIComponent(window.location.href)
        window.location.href = `${getLoginAppUrl()}/auth/login?redirect=${redirectUrl}&loggedOut=true`
    }
}

/**
 * Call this when an API response is 401 (e.g. token revoked). Clears auth and redirect to login app.
 * Usage: const res = await fetch(...); if (res.status === 401) { handleUnauthorized(); return; }
 */
export function handleUnauthorized(): void {
    redirectToLoginApp()
}

/** Decide whether a fetch URL is to an askfinz.ai backend so we know to
 * include the V-029 HttpOnly cookie. */
function _isAskfinzApiUrl(input: RequestInfo | URL): boolean {
    try {
        let urlStr: string
        if (typeof input === 'string') urlStr = input
        else if (input instanceof URL) urlStr = input.toString()
        else urlStr = (input as Request).url
        // Relative URLs are always same-origin → safe to include credentials.
        if (urlStr.startsWith('/')) return true
        const u = new URL(urlStr, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
        return u.hostname.endsWith('askfinz.ai') || u.hostname === 'localhost' || u.hostname === '127.0.0.1'
    } catch {
        return false
    }
}

/**
 * Fetch wrapper that redirects to the login app on 401. Use for authenticated backend API calls
 * so that revoked tokens (e.g. after logout elsewhere) log the user out here too.
 *
 * SECURITY V-029: we now also send `credentials: 'include'` for all
 * *.askfinz.ai (and localhost) requests so the server-set HttpOnly cookie
 * goes along with the request automatically. The Authorization header is
 * still added for backward compat (multi-account / legacy localStorage
 * tokens). On the backend `utils/auth_deps.py` prefers the header over the
 * cookie.
 */
export async function authFetch(
    input: RequestInfo | URL,
    init: RequestInit & { skipAuthRedirect?: boolean } = {}
): Promise<Response> {
    // Background/non-critical callers (notification bell, preference sync) pass
    // skipAuthRedirect so a flaky cross-origin account 401 does NOT force a
    // fleet-wide logout. Per redirectToLoginApp's own contract: "Background-API
    // 401s must NOT clear the domain-wide session marker." Critical callers
    // omit it and keep the redirect-on-401 behaviour.
    const skipAuthRedirect = init.skipAuthRedirect === true
    const token = getAuthToken()
    const headers = new Headers(init.headers || {})

    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`)
    }

    const finalInit: RequestInit = { ...init, headers }
    if (init.credentials === undefined && _isAskfinzApiUrl(input)) {
        finalInit.credentials = 'include'
    }

    let res = await fetch(input, finalInit)
    if (res.status === 401 && _isAskfinzApiUrl(input)) {
        // SECURITY V-025: try ONE refresh round-trip before giving up. The
        // singleflight refreshAccessToken() makes concurrent 401s coalesce
        // onto a single /api/auth/refresh call. If the refresh succeeds the
        // browser now has a fresh access cookie; we replay the original
        // request once. On second 401 we hard-redirect to the login app.
        const ok = await refreshAccessToken()
        if (ok) {
            // After a successful refresh the browser has a new access cookie.
            // Rebuild headers WITHOUT the stale Authorization so the retry
            // relies on the fresh HttpOnly cookie — a stale/invalid Bearer
            // would override the valid cookie and produce another 401.
            const retryHeaders = new Headers(finalInit.headers || {})
            retryHeaders.delete("Authorization")
            const freshToken = getAuthToken()
            if (freshToken) retryHeaders.set("Authorization", `Bearer ${freshToken}`)
            res = await fetch(input, { ...finalInit, headers: retryHeaders })
        }
        if (res.status === 401 && !skipAuthRedirect) {
            redirectToLoginApp()
        }
    } else if (res.status === 401 && !skipAuthRedirect) {
        redirectToLoginApp()
    }
    return res
}

const AUTH_COOKIE_MAX_AGE_DAYS = 7

/** Set the cross-subdomain session marker so middleware + UI can answer
 * "is the user logged in?" without reading the JWT.
 *
 * SECURITY V-029: we no longer write the actual JWT to a JS-readable cookie
 * here — the backend sets `AskFinz_auth_token` HttpOnly on every token-
 * minting endpoint, and that's the source of truth for browser API calls.
 * Instead we publish a short non-secret marker (truncated token hash) to a
 * regular cookie so legacy middleware and UI gates keep working.
 *
 * NOTE: middleware that reads the cookie name `AskFinz_auth_token` directly
 * will now see the server's HttpOnly value (browsers send it on same-origin
 * Next.js middleware requests too). We deliberately do NOT overwrite that
 * cookie from JS — doing so would clear the HttpOnly flag.
 */
export function setAuthCookie(token: string): void {
    if (typeof document === 'undefined') return
    const maxAge = AUTH_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60

    // Determine domain for cookie sharing
    let domainAttr = ''
    if (typeof window !== 'undefined' && !window.location.hostname.includes('localhost')) {
        // If on askfinz.ai (or subdomains), set it to the base domain
        if (window.location.hostname.endsWith('askfinz.ai')) {
            domainAttr = '; Domain=.askfinz.ai'
        }
    }

    // SECURITY V-029: publish only a non-secret 16-char hash as the marker.
    // Anyone reading this cookie cannot reconstruct the JWT.
    let marker = ''
    try {
        // Use a tiny FNV-1a-style fold so we don't depend on SubtleCrypto here.
        let h1 = 0x811c9dc5
        let h2 = 0x9e3779b1
        for (let i = 0; i < token.length; i++) {
            h1 ^= token.charCodeAt(i); h1 = Math.imul(h1, 0x01000193) >>> 0
            h2 ^= token.charCodeAt(i); h2 = Math.imul(h2, 0x85ebca77) >>> 0
        }
        marker = (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 16)
    } catch { marker = '1' }
    document.cookie = `${AUTH_SESSION_MARKER_COOKIE}=${marker}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}`

    // SECURITY V-029: deliberately NOT writing AUTH_COOKIE_NAME from JS — the
    // server-set HttpOnly cookie is authoritative. Overwriting from JS would
    // strip the HttpOnly+Secure flags and re-open the localStorage XSS path.
    //
    // Local-dev fallback: when running on http://localhost:* the backend's
    // Secure cookie won't stick (browsers refuse Secure on plain HTTP). To
    // keep `npm run dev` working we still drop the JWT into a non-Secure
    // cookie there only.
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(token)}; path=/; max-age=${maxAge}; SameSite=Lax`
    }
}

export function isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false

    // SECURITY V-029: prefer the non-HttpOnly fingerprint marker so a UI gate
    // can answer "logged in?" without ever reading the JWT. Fall back to the
    // legacy localStorage / JS-cookie lookup so multi-account flows that
    // haven't issued a marker yet keep working.
    if (typeof document !== 'undefined') {
        const marker = getCookie(AUTH_SESSION_MARKER_COOKIE)
        if (marker) return true
        // Server-set HttpOnly cookie is invisible to JS — but if it's there
        // the marker should also be there once setAuthCookie() ran. To be
        // safe, also accept a JS-readable AUTH_COOKIE_NAME (localhost dev).
        if (getCookie(AUTH_COOKIE_NAME)) return true
    }
    const token = getAuthToken()
    return !!token
}

/** Get cookie by name. */
export function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const nameEQ = name + "="
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        while (c.charAt(0) === ' ') c = c.substring(1, c.length)
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length))
    }
    return null
}

export function getAuthToken(): string | null {
    if (typeof window === 'undefined') return null

    try {
        const urlParams = new URLSearchParams(window.location.search)

        // SECURITY V-008: legacy URL handoff via `?token=` / `?add_token=`
        // is CLOSED. The backend's `auth_deps.get_current_user` already
        // refuses query-string Bearer tokens (V-006). We additionally
        // ignore them here: log a warning, strip the param so it doesn't
        // linger in the URL, and rely on the V-008 `#handoff=<code>`
        // fragment + consumeHandoffFromHash() for cross-subdomain auth.
        let strippedLegacy = false
        if (urlParams.has('add_token') || urlParams.has('token')) {
            console.warn(
                '[Auth V-008] Legacy ?token=/?add_token= URL parameters are no '
                + 'longer accepted; use the #handoff=<code> single-use code flow '
                + 'instead. Stripping the param.'
            )
            urlParams.delete('add_token')
            urlParams.delete('token')
            try {
                const qs = urlParams.toString()
                const cleaned = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
                window.history.replaceState({}, '', cleaned)
            } catch { /* ignore */ }
            strippedLegacy = true
        }

        // Only return a token from localStorage if it looks like a real JWT.
        // Non-JWT values like "null" (the string), "handoff:..." placeholders, or
        // opaque session IDs must never be sent as a Bearer token — the backend
        // rejects them with 401, which triggers a logout redirect.
        const localToken = localStorage.getItem(AUTH_TOKEN_KEY)
        if (localToken) {
            if (localToken.split('.').length === 3 && localToken.startsWith('eyJ')) {
                return localToken
            }
            // Stale / corrupt value — clear it so the cookie fallback can take over.
            try { localStorage.removeItem(AUTH_TOKEN_KEY) } catch { /* ignore */ }
        }

        // NOTE: do NOT early-return here when strippedLegacy is true.
        // The Next.js middleware (auth-middleware.ts) reads the ?token= param and
        // immediately writes it as AskFinz_auth_token cookie on the same response.
        // By the time client JS runs, that cookie is already in the browser — we
        // must check it here or the user appears unauthenticated and gets redirected
        // to login even though they just performed a valid cross-subdomain handoff.

        // Fallback to cookie (the middleware sets AskFinz_auth_token from ?token=;
        // the backend sets it HttpOnly on production). Only promote a real JWT —
        // never re-hydrate a non-JWT placeholder.
        const cookieToken = getCookie(AUTH_COOKIE_NAME)
        if (cookieToken && cookieToken.split('.').length === 3 && cookieToken.startsWith('eyJ')) {
            // Sync to localStorage so future calls don't need the cookie fallback.
            try { localStorage.setItem(AUTH_TOKEN_KEY, cookieToken) } catch { /* ignore */ }
            return cookieToken
        }
        return null
    } catch (error) {
        console.error('[Auth] Error reading token:', error)
        return null
    }
}

export function getAuthUser(): any | null {
    if (typeof window === 'undefined') return null

    try {
        const userData = localStorage.getItem(AUTH_USER_KEY)
        return userData ? JSON.parse(userData) : null
    } catch (error) {
        console.error('[Auth] Error reading user data from localStorage:', error)
        return null
    }
}

/** Build sidebar user (name + email + avatar) from stored auth user. Use for AppShell user prop. */
export function getSidebarUser(): { name: string; email: string; avatar: string; preferences?: any } | undefined {
    const u = getAuthUser()
    
    // Robust field mapping for name
    let name = 'User'
    let email = ''
    let avatar = ''
    let preferences = {}

    if (u && typeof u === 'object') {
        name = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : (u.firstName || u.lastName || u.name || u.username || u.email || 'User')
        email = u.email ?? ''
        avatar = u.avatar || u.avatarUrl || u.picture || ''
        preferences = u.preferences || {}
    } else if (typeof document !== 'undefined') {
        // Fallback to shared cookies for inter-subdomain sync when localStorage is empty
        name = getCookie('AskFinz_user_name') || 'User'
        email = getCookie('AskFinz_user_email') || ''
        avatar = getCookie('AskFinz_user_avatar') || ''
        
        // If we don't even have a name from cookies, we probably aren't logged in
        if (name === 'User' && !email && !isAuthenticated()) return undefined
    } else {
        return undefined
    }

    return { name: String(name), email: String(email), avatar: String(avatar), preferences }
}

import { useState, useEffect } from 'react'

/** Hook to get sidebar user data reactively. */
export function useSidebarUser() {
    // Start with undefined on server and client to avoid hydration mismatch (#418).
    // User data is loaded client-side only inside useEffect.
    const [user, setUser] = useState<{ name: string; email: string; avatar: string; preferences?: any } | undefined>(undefined)

    useEffect(() => {
        // SECURITY V-008: before checking any local auth state, see if the
        // URL fragment carries a single-use handoff code and redeem it. If
        // so, the server has just set our HttpOnly cookie — we proceed as
        // if the user were already logged in.
        consumeHandoffFromHash().then(consumed => {
            if (consumed) {
                // Re-fetch the user from the server now that the cookie is set.
                fetchCurrentUserFromApi().then(apiUser => {
                    if (apiUser) {
                        setUser(apiUser)
                    } else {
                        setUser(getSidebarUser())
                    }
                }).catch(() => setUser(getSidebarUser()))
            } else {
                setUser(getSidebarUser())
            }
        }).catch(() => setUser(getSidebarUser()))

        const handler = () => {
            const newUser = getSidebarUser()
            setUser(prev => {
                if (JSON.stringify(prev) === JSON.stringify(newUser)) return prev
                return newUser
            })
        }

        let lastFetchTime = 0
        const FETCH_THROTTLE_MS = 5000 // Only fetch once every 5 seconds per app instance

        const refreshData = () => {
             const now = Date.now()
             if (now - lastFetchTime < FETCH_THROTTLE_MS) return

             if (isAuthenticated()) {
                lastFetchTime = now
                fetchCurrentUserFromApi().then(apiUser => {
                    if (apiUser) {
                        const token = getAuthToken()
                        if (token) {
                            const existingUser = getAuthUser() || {}

                            // Only update if data actually changed to avoid dispatching events in a loop
                            const nextUser = { ...existingUser, ...apiUser }
                            if (JSON.stringify(existingUser) !== JSON.stringify(nextUser)) {
                                setAuthToken(token, nextUser)
                            }
                        }
                    }
                })
            }
        }

        // Always attempt to fetch fresh data from API on mount
        refreshData()

        window.addEventListener('authUserUpdated', handler)
        
        // Listen for storage events from other tabs (same origin)
        const storageHandler = (e: StorageEvent) => {
            if (e.key === AUTH_USER_KEY || e.key === AUTH_TOKEN_KEY) {
                handler()
            }
        }
        window.addEventListener('storage', storageHandler)

        // Sync on window focus (inter-subdomain/inter-tab sync)
        const focusHandler = () => refreshData()
        window.addEventListener('focus', focusHandler)

        return () => {
            window.removeEventListener('authUserUpdated', handler)
            window.removeEventListener('storage', storageHandler)
            window.removeEventListener('focus', focusHandler)
        }
    }, [])

    return user
}

/** Fetch current user (name, email, avatar, preferences) from FastAPI GET /api/auth/me.
 *
 * SECURITY V-008 / V-029: cookie-only (no Bearer header) is supported — the
 * V-029 HttpOnly cookie travels via `credentials: 'include'`. The Bearer
 * header is added only when a JS-readable token is still around (legacy
 * multi-account state). Returns undefined on error or unauthenticated.
 */
export async function fetchCurrentUserFromApi(): Promise<{ name: string; email: string; avatar: string; preferences?: any } | undefined> {
    if (typeof window === 'undefined') return undefined
    const token = getAuthToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    try {
        // ALWAYS fetch from central account API
        const res = await fetch(`${getAccountApiUrl()}/api/auth/me`, {
            headers,
            credentials: 'include',
        })
        if (!res.ok) return undefined
        const data = await res.json()
        return {
            name: data?.name ?? 'User',
            email: data?.email ?? '',
            avatar: data?.avatar ?? '',
            preferences: data?.preferences ?? {},
        }
    } catch {
        return undefined
    }
}

/** Update user preferences on the backend. Matches Partial<User.preferences>. */
export async function updateUserPreferences(preferences: Record<string, any>): Promise<boolean> {
    if (typeof window === 'undefined') return false
    const token = getAuthToken()
    if (!token) return false

    try {
        // We use PATCH /api/account/profile which now merges preferences on the backend
        // ALWAYS sync to the central account API
        const res = await authFetch(`${getAccountApiUrl()}/api/account/profile`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ preferences }),
            // Background preference sync — a 401 here must not force a logout.
            skipAuthRedirect: true,
        })

        if (res.ok) {
            // Update local user data too to stay in sync without fresh fetch
            const currentUser = getAuthUser()
            if (currentUser) {
                const newPreferences = { ...(currentUser.preferences || {}), ...preferences }
                setAuthToken(token, { ...currentUser, preferences: newPreferences })
            }
            return true
        }
        return false
    } catch (err) {
        console.error('[Auth] Failed to update preferences:', err)
        return false
    }
}

export function setAuthToken(token: string, userData?: any): void {
    if (typeof window === 'undefined') return

    try {
        // Guard: only persist real JWTs. A non-JWT value (null coerced to the
        // string "null", a "handoff:…" placeholder, etc.) written here would be
        // read back by getAuthToken() and sent as "Bearer null" on every API
        // call, producing a permanent 401 → logout loop.
        if (!token || token.split('.').length !== 3 || !token.startsWith('eyJ')) {
            return
        }
        localStorage.setItem(AUTH_TOKEN_KEY, token)
        if (userData) {
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData))
            
            // Set shared cookies for immediate inter-subdomain sync of basic UI elements
            if (typeof document !== 'undefined') {
                const maxAge = 7 * 24 * 60 * 60
                let domainAttr = ''
                if (window.location.hostname.endsWith('askfinz.ai')) {
                    domainAttr = '; Domain=.askfinz.ai'
                }

                const setShared = (name: string, val: string) => {
                    document.cookie = `${name}=${encodeURIComponent(val)}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}`
                }

                const sidebar = getSidebarUser()
                if (sidebar) {
                    setShared('AskFinz_user_name', sidebar.name)
                    setShared('AskFinz_user_email', sidebar.email)
                    setShared('AskFinz_user_avatar', sidebar.avatar)
                    if (sidebar.preferences?.theme) {
                        setShared('AskFinz_theme', sidebar.preferences.theme)
                    }
                }
            }
        }
        setAuthCookie(token)
        // Keep the multi-account list in sync. Lazy import to avoid a static
        // circular dep — multi-account imports from this file. Safe (no
        // recursion) because multi-account.setActiveAccount writes legacy
        // keys directly (does not call setAuthToken).
        import('./multi-account').then(({ addAccount }) => {
            try { addAccount(token, userData ?? {}) } catch { /* ignore */ }
        }).catch(() => { /* ignore */ })
        window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: userData || null }))
    } catch (error) {
        console.error('[Auth] Error saving token to localStorage:', error)
    }
}

/**
 * SECURITY V-008 — request a single-use handoff code and return the
 * destination URL with `#handoff=<code>` baked into the fragment.
 *
 * The caller is expected to navigate the browser to the returned URL.
 * On the destination side `consumeHandoffFromHash()` should run in the
 * app bootstrap before any auth check; it'll redeem the code, the server
 * will set the V-029 HttpOnly cookie, and the destination is logged in
 * without ever exposing a JWT in the URL bar / browser history / Referer.
 */
export async function requestHandoff(
    destination: string,
    mode: 'replace' | 'add' = 'replace'
): Promise<string> {
    if (typeof window === 'undefined') {
        throw new Error('requestHandoff requires a browser environment')
    }
    const res = await authFetch(`${getAccountApiUrl()}/api/auth/handoff/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination, mode }),
        credentials: 'include',
    })
    if (!res.ok) {
        let detail = ''
        try { detail = (await res.json()).detail || '' } catch { /* ignore */ }
        throw new Error(`handoff issue failed (${res.status}): ${detail}`)
    }
    const data = await res.json()
    if (!data?.redirect_url) {
        throw new Error('handoff issue returned no redirect_url')
    }
    return data.redirect_url as string
}


/**
 * SECURITY V-008 — on app boot, look at `location.hash` for `#handoff=...`
 * and redeem it. Returns true if a handoff code was consumed (caller can
 * skip its own auth bootstrap and treat the user as logged in).
 *
 * The redeem POST sends `credentials: 'include'` so the destination's
 * HttpOnly cookie is set by the server response. We then strip the hash so
 * the code never lingers in the URL, history, or copy-paste of the URL.
 *
 * Multi-account: when `mode === 'add'` we DON'T overwrite the active
 * identity — we register the new identity via `addAccount(...)` (the JWT
 * is no longer needed in JS, so we pass an empty token + identity metadata
 * only). The active session is whichever cookie the server most recently
 * wrote.
 */
export async function consumeHandoffFromHash(): Promise<boolean> {
    if (typeof window === 'undefined') return false
    const hash = window.location.hash || ''
    if (!hash || !hash.includes('handoff=')) return false
    // Hash may contain other params: parse like a search string after `#`.
    let codeStr = ''
    try {
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
        codeStr = params.get('handoff') || ''
        if (!codeStr) return false
        params.delete('handoff')
        const remaining = params.toString()
        const newHash = remaining ? `#${remaining}` : ''
        const cleanedUrl = window.location.pathname + window.location.search + newHash
        window.history.replaceState({}, '', cleanedUrl)
    } catch {
        return false
    }
    try {
        const res = await fetch(`${getAccountApiUrl()}/api/auth/handoff/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code: codeStr }),
        })
        if (!res.ok) {
            console.warn('[Auth V-008] handoff redeem failed:', res.status)
            return false
        }
        const data = await res.json()
        // Clear legacy localStorage token BEFORE registering the new identity
        // so there is zero window where getAuthToken() returns the placeholder
        // that addAccount() writes. The HttpOnly cookie is the real auth carrier.
        try { localStorage.removeItem('AskFinz_auth_token') } catch { /* ignore */ }
        // Multi-account: register the identity in the device list. The token
        // field uses a "handoff:<identity>" placeholder — setActiveAccount()
        // guards against writing non-JWT tokens to localStorage/cookie, so
        // only the user metadata (name, email, avatar) propagates to legacy
        // storage. The HttpOnly cookie is the canonical session credential.
        try {
            const { addAccount } = await import('./multi-account')
            const u = data?.user || {}
            const identity = data?.identity_id || u.email || u.user_id
            if (identity) {
                addAccount(`handoff:${identity}`, {
                    email: u.email || identity,
                    name: u.name || u.full_name || u.email || identity,
                    avatar: u.avatar || '',
                    user_id: u.user_id || data?.user_id || '',
                })
            }
        } catch (err) {
            console.warn('[Auth V-008] multi-account addAccount failed:', err)
        }
        // Second removeItem as a safety net in case setActiveAccount wrote
        // the placeholder despite the _isJwt guard (e.g. in older bundles).
        try { localStorage.removeItem('AskFinz_auth_token') } catch { /* ignore */ }
        // Notify listening UI components.
        try {
            window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: data?.user ?? null }))
        } catch { /* ignore */ }
        return true
    } catch (err) {
        console.warn('[Auth V-008] handoff redeem error:', err)
        return false
    }
}


/**
 * SECURITY V-025 — refresh the access cookie via /api/auth/refresh.
 *
 * Wrapped in a singleflight Promise so concurrent 401s from many in-flight
 * fetches don't stampede the refresh endpoint. Resolves to true on success,
 * false on any failure (including 401 — the caller should redirect to
 * login in that case).
 */
let _refreshInflight: Promise<boolean> | null = null

export function refreshAccessToken(): Promise<boolean> {
    if (_refreshInflight) return _refreshInflight
    if (typeof window === 'undefined') return Promise.resolve(false)
    _refreshInflight = (async () => {
        try {
            const res = await fetch(`${getAccountApiUrl()}/api/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
            })
            return res.ok
        } catch {
            return false
        } finally {
            // Clear singleflight after a tick so subsequent requests get a
            // fresh attempt.
            setTimeout(() => { _refreshInflight = null }, 0)
        }
    })()
    return _refreshInflight
}


export function clearAuthToken(): void {
    if (typeof window === 'undefined') return

    try {
        localStorage.removeItem(AUTH_TOKEN_KEY)
        localStorage.removeItem(AUTH_USER_KEY)
        localStorage.removeItem('askfinz_auth_token') // Also clear lowercase variant

        const nukeCookie = (name: string) => {
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${window.location.hostname}; SameSite=Lax`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.askfinz.ai`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=.askfinz.ai; SameSite=Lax`
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=localhost`
        }

        nukeCookie(AUTH_COOKIE_NAME)
        nukeCookie(AUTH_SESSION_MARKER_COOKIE)
        nukeCookie('askfinz_auth_token')
        nukeCookie('AskFinz_user_name')
        nukeCookie('AskFinz_user_email')
        nukeCookie('AskFinz_user_avatar')
        nukeCookie('AskFinz_theme')
        // Multi-account shared-cookie state (cross-subdomain canonical list).
        // clearAuthToken is the full-logout path; multi-account.removeAccount
        // handles the partial case (and only calls us when no accounts remain).
        nukeCookie('AskFinz_accounts_v1')
        nukeCookie('AskFinz_active_account_v1')
        try { localStorage.removeItem('AskFinz_accounts') } catch { /* ignore */ }
        try { localStorage.removeItem('AskFinz_active_account_id') } catch { /* ignore */ }

        window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: null }))
    } catch (error) {
        console.error('[Auth] Error clearing token from localStorage:', error)
    }
}
